import { IdleIncluded } from '@evi/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ArrowLeft, CalendarClock, Zap } from 'lucide-react';
import { ThroughputChart, type ThroughputChartPeriod } from '@evi/ui';
import {
  fetchMyChargerSessions,
  fetchMyChargerThroughputSeries,
  type MyChargerSession,
} from '@/lib/api';
import { formatMoney } from '@/lib/money';
import { StatusPill } from '@/components/primitives';
import NamedIcon from '@/components/ui/NamedIcon';
import styles from './MyChargerSessionsPage.module.css';

interface Props {
  chargerId: string;
  onNavigate: (path: string) => void;
}

/**
 * Sessions list for a resident-owned charger. Resident-tier view:
 * who charged here, when, how much energy, what they paid. Guest
 * sessions are explicitly tagged with the "Guest pricing" badge so
 * the resident can correlate the row with the markup they set.
 *
 * Owner-self sessions (the resident charging on their own charger)
 * render with name "Owner" — the API leaves user_name null when no
 * driver was charged (system-stopped or anonymous), and on the
 * resident-charges-self path we still want the row to read as a
 * familiar entity rather than a blank cell.
 */
export function MyChargerSessionsPage({ chargerId, onNavigate }: Props) {
  const { t } = useTranslation('myChargers');
  const { data, isLoading, error } = useQuery({
    queryKey: ['my-charger', chargerId, 'sessions'],
    queryFn: () => fetchMyChargerSessions(chargerId),
    staleTime: 30_000,
  });
  const sessions = data ?? [];

  // Throughput chart period state. Default '7d' matches the admin
  // landing convention (e1f7e91) and the sibling MyChargerEarningsPage
  // chart's default. `keepPreviousData` holds the previous response
  // visible during period flips so the chart doesn't flash to its
  // empty state during the ~100-300ms refetch — same pattern from
  // LocationInfoCard (Phase 3 of the admin expansion).
  const [chartPeriod, setChartPeriod] = useState<ThroughputChartPeriod>('7d');
  const throughputQ = useQuery({
    queryKey: ['my-charger', chargerId, 'throughput-series', chartPeriod],
    queryFn: () => fetchMyChargerThroughputSeries(chargerId, chartPeriod),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backFab}
          aria-label={t('sessions.back')}
          onClick={() => onNavigate(`/my-chargers/${chargerId}`)}
        >
          <ArrowLeft size={16} />
        </button>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>{t('sessions.title')}</h1>
          <p className={styles.subtitle}>
            {isLoading
              ? t('sessions.loading')
              : sessions.length === 0
                ? t('sessions.subtitle.empty')
                : t('sessions.subtitle.count', { count: sessions.length })}
          </p>
        </div>
      </header>

      {/* Throughput chart — sessions bars + kWh line, period-toggled.
          Renders above the per-session list so the chart gives
          context (utilisation trend) for the list rows below. Hidden
          on first fetch; keepPreviousData keeps it visible across
          period flips. Uses the chart's default footer ("N sessions
          · X.X KWH" inline) — no separate footer pill since the
          chart's built-in summary already covers both metrics; an
          extra pill would duplicate signal. */}
      {throughputQ.data && (
        <div className={styles.chartWrap}>
          <ThroughputChart
            data={throughputQ.data.buckets}
            period={chartPeriod}
            onPeriodChange={setChartPeriod}
            allowedPeriods={['24h', '7d', '30d']}
          />
        </div>
      )}

      {isLoading && (
        <div className={styles.list}>
          <div className={styles.cardSkeleton} />
          <div className={styles.cardSkeleton} />
          <div className={styles.cardSkeleton} />
        </div>
      )}

      {!isLoading && error && (
        <div className={styles.errorBox}>
          {error instanceof Error ? error.message : t('sessions.errorLoad')}
        </div>
      )}

      {!isLoading && !error && sessions.length === 0 && (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>
            {/* MEDIUM 6.2 — consolidated with calendar.json (identical bytes). */}
            <NamedIcon name="calendar" fallback={<CalendarClock size={40} />} size={40} />
          </div>
          <h2 className={styles.emptyTitle}>{t('sessions.empty.title')}</h2>
          <p className={styles.emptyDesc}>
            {t('sessions.empty.desc')}
          </p>
        </div>
      )}

      {!isLoading && !error && sessions.length > 0 && (
        <div className={styles.list}>
          {sessions.map((s) => <SessionCard key={s._id} session={s} />)}
        </div>
      )}
    </div>
  );
}

function SessionCard({ session }: { session: MyChargerSession }) {
  const { t, i18n } = useTranslation('myChargers');
  // "Owner" label when there's no separately-identified driver
  // (resident-self sessions and a few system-stopped edge cases).
  const driverLabel = session.user_name ?? t('sessions.owner');
  const startedAt = new Date(session.start_time);

  return (
    <article className={styles.card}>
      <header className={styles.cardHeader}>
        <div className={styles.cardWho}>
          <p className={styles.cardName}>{driverLabel}</p>
          <p className={styles.cardWhen}>{formatDateTime(startedAt)}</p>
        </div>
        {session.was_guest && (
          <StatusPill tone="accent" variant="soft" size="sm">
            {t('sessions.guestPricing')}
          </StatusPill>
        )}
      </header>
      <div className={styles.cardStats}>
        <div className={styles.stat}>
          <span className={styles.statLabel}>{t('sessions.stat.energy')}</span>
          <span className={styles.statValue}>
            <Zap size={18} className={styles.statIcon} />
            {session.kwh_consumed.toFixed(2)} kWh
          </span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>{t('sessions.stat.total')}</span>
          <span className={styles.statValue}>
            {/* In the session's own currency (item 3); absent reads USD. */}
            <span>{formatMoney(session.total_cost_cents, session.currency, i18n.language)}
              <IdleIncluded cents={session.idle_fee_cents} currency={session.currency} locale={i18n.language} /></span>
          </span>
        </div>
      </div>
    </article>
  );
}

function formatDateTime(d: Date): string {
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
